import { useState, useMemo } from 'react'
import DismissButton from 'components/dismiss-button'
import { BlueButton } from 'components/blue-button'
import { showToast } from 'components/toast'
import { Lesson, Class } from '@all-for-one/shared-types'
import { WorksheetPdfDocument, WorksheetEntry } from './pdf-worksheet'
import { collectWorksheetImageUrls } from './pdf-content-selection'
import { getLogoPngDataUrl, getPdfDownloadDate, downloadPdf, buildImageMap } from 'util/pdf-download'
import printStyles from 'css/components/lesson-builder/print-overlay.module.css'

interface PrintWorksheetsOverlayProps {
    lesson: Lesson
    assignedClassName?: string
    gradeBand?: string
    subject?: string
    classData?: Class
    onDismiss: () => void
}

const LEVELS = ['Emerging', 'Intermediate', 'Advanced'] as const

const SECTION_ORDER = [
    'Pre-Assessment',
    'Check for Understanding',
    'Independent Work',
    'Post-Assessment',
] as const

export const PrintWorksheetsOverlay = ({
    lesson,
    assignedClassName,
    gradeBand,
    subject,
    classData,
    onDismiss,
}: PrintWorksheetsOverlayProps) => {
    const [step, setStep] = useState(1)
    const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set())
    const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set())
    const [sectionMode, setSectionMode] = useState<'together' | 'separate'>('together')
    const [downloading, setDownloading] = useState(false)

    const studentCounts = useMemo(() => {
        const counts: Record<string, number> = { Emerging: 0, Intermediate: 0, Advanced: 0 }
        if (classData?.students) {
            for (const student of classData.students) {
                const d = student.difficulty as string
                if (d in counts) counts[d]++
            }
        }
        return counts
    }, [classData])

    const availableSections = useMemo(() => {
        const sections = new Set<string>()
        for (const qs of lesson.questionSlides || []) {
            if (qs.learningStage) sections.add(qs.learningStage)
        }
        return SECTION_ORDER.filter(s => sections.has(s))
    }, [lesson])

    const toggleLevel = (level: string) => {
        setSelectedLevels(prev => {
            const next = new Set(prev)
            if (next.has(level)) next.delete(level)
            else next.add(level)
            return next
        })
    }

    const toggleSection = (section: string) => {
        setSelectedSections(prev => {
            const next = new Set(prev)
            if (next.has(section)) next.delete(section)
            else next.add(section)
            return next
        })
    }

    const canProceed = () => {
        if (step === 1) return selectedLevels.size > 0
        if (step === 2) return selectedSections.size > 0
        return true
    }

    const summaryParts = useMemo(() => {
        const orderedLevels = LEVELS.filter(l => selectedLevels.has(l))
        const orderedSections = SECTION_ORDER.filter(s => selectedSections.has(s))
        if (sectionMode === 'together') {
            return orderedLevels.map(level => `${level} (${orderedSections.join(', ')})`)
        }
        const parts: string[] = []
        for (const level of orderedLevels) {
            for (const section of orderedSections) {
                parts.push(`${level} — ${section}`)
            }
        }
        return parts
    }, [selectedLevels, selectedSections, sectionMode])

    const totalFiles = useMemo(() => {
        if (sectionMode === 'together') return selectedLevels.size
        return selectedLevels.size * selectedSections.size
    }, [selectedLevels, selectedSections, sectionMode])

    const handleDownload = async () => {
        setDownloading(true)
        try {
            const orderedLevels = LEVELS.filter(l => selectedLevels.has(l))
            const orderedSections = SECTION_ORDER.filter(s => selectedSections.has(s))
            const [logoDataUrl, imageMap] = await Promise.all([
                getLogoPngDataUrl(),
                buildImageMap(collectWorksheetImageUrls(lesson, orderedLevels, orderedSections as unknown as string[])),
            ])
            const downloadDate = getPdfDownloadDate()

            for (const level of orderedLevels) {
                if (sectionMode === 'together') {
                    const worksheets: WorksheetEntry[] = [{ level, sections: orderedSections as unknown as string[] }]
                    const doc = (
                        <WorksheetPdfDocument
                            lesson={lesson}
                            assignedClassName={assignedClassName}
                            gradeBand={gradeBand}
                            subject={subject}
                            logoDataUrl={logoDataUrl}
                            downloadDate={downloadDate}
                            worksheets={worksheets}
                            level={level}
                            imageMap={imageMap}
                        />
                    )
                    await downloadPdf(doc, `${lesson.name}_${level}.pdf`)
                } else {
                    for (const section of orderedSections) {
                        const worksheets: WorksheetEntry[] = [{ level, sections: [section] }]
                        const doc = (
                            <WorksheetPdfDocument
                                lesson={lesson}
                                assignedClassName={assignedClassName}
                                gradeBand={gradeBand}
                                subject={subject}
                                logoDataUrl={logoDataUrl}
                                downloadDate={downloadDate}
                                worksheets={worksheets}
                                level={level}
                                imageMap={imageMap}
                            />
                        )
                        await downloadPdf(doc, `${lesson.name}_${level}_${section}.pdf`)
                    }
                }
            }
            onDismiss()
        } catch (error) {
            console.error('Error generating worksheets:', error)
            showToast("We couldn't generate those worksheets. Please try again.")
        } finally {
            setDownloading(false)
        }
    }

    return (
        <div className="overlay">
            <div className="overlay-content-container">
                <div className="overlay-content-header">
                    Download Student Worksheets
                    <DismissButton onClick={onDismiss} />
                </div>
                <div className="overlay-content-body">
                    {step === 1 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Which levels?</h3>
                            {LEVELS.map(level => (
                                <label key={level} className={printStyles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={selectedLevels.has(level)}
                                        onChange={() => toggleLevel(level)}
                                    />
                                    {level}{classData?.students && classData.students.length > 0
                                        ? ` (${studentCounts[level]} students)`
                                        : ''}
                                </label>
                            ))}
                            <div className={printStyles.navRow}>
                                <div />
                                <BlueButton label="Next" onClick={() => setStep(2)} disabled={!canProceed()} />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Which sections?</h3>
                            {availableSections.map(section => (
                                <label key={section} className={printStyles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={selectedSections.has(section)}
                                        onChange={() => toggleSection(section)}
                                    />
                                    {section}
                                </label>
                            ))}
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(1)}>Back</button>
                                <BlueButton label="Next" onClick={() => setStep(3)} disabled={!canProceed()} />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>How would you like to organize the sections?</h3>
                            <label className={printStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="sectionMode"
                                    checked={sectionMode === 'together'}
                                    onChange={() => setSectionMode('together')}
                                />
                                All sections together (one file per level)
                            </label>
                            <label className={printStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="sectionMode"
                                    checked={sectionMode === 'separate'}
                                    onChange={() => setSectionMode('separate')}
                                />
                                Sections as separate files
                            </label>
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(2)}>Back</button>
                                <BlueButton label="Next" onClick={() => setStep(4)} />
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Summary</h3>
                            <p className={printStyles.summaryText}>
                                You are downloading <strong>{totalFiles}</strong> {totalFiles === 1 ? 'file' : 'files'}:
                            </p>
                            <ul className={printStyles.summaryList}>
                                {summaryParts.map((part, i) => <li key={i}>{part}</li>)}
                            </ul>
                            <p className={printStyles.summaryNote}>
                                Each worksheet's level is shown in the footer: <strong>E</strong> for Emerging, <strong>I</strong> for Intermediate, <strong>A</strong> for Advanced.
                            </p>
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(3)}>Back</button>
                                <BlueButton label={downloading ? 'Generating…' : 'Download'} onClick={handleDownload} disabled={downloading} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
